/*
 * Interactive HVACloud AI console. Sits above the free-text hint in the
 * task modal; collapsed by default so operators who just want to fire off
 * a one-shot suggestion don't see it. When opened, the operator can chat
 * with HVACloud AI to clarify what the actual suggestion should look like.
 *
 * Guardrails are deliberately server-side: the /api/ai/chat/turn endpoint
 * forces the model through a single tool contract, and the system prompt
 * refuses off-topic content. If a turn comes back with in_scope=false, we
 * render it as a red-tinted refusal so the operator sees the boundary.
 *
 * On collapse or on "Vorschlagen", the transcript is compressed into a
 * hint block that the outer AiTask* modals append to their existing
 * operator-hint field.
 */

import { useEffect, useRef, useState } from 'preact/hooks';
import { api, ApiError } from '../../api/client';
import { BRAND } from '../../brand/brand';

export type AiChatMode = 'schema' | 'units' | 'dashboard';

export interface AiConsoleContext {
  mode: AiChatMode;
  plantId?: string;
  deviceId?: string;
}

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  refused?: boolean;
}

interface Reply {
  text: string;
  inScope: boolean;
  readyToPropose: boolean;
}

interface Props {
  context: AiConsoleContext;
  /** Called whenever the transcript changes; parent uses this to merge the
   *  chat into its hint field on Apply. */
  onTranscriptChange(hint: string): void;
  /** True once the assistant has said "genug Kontext" or the user forced it.
   *  Parent may auto-open its own Vorschlagen button state accordingly. */
  onReady?(): void;
}

const GREETING: Record<AiChatMode, string> = {
  schema:    'Hallo! Ich helfe dir, die Datenpunkte deiner Anlage sinnvoll zu gruppieren. Gibt es Präferenzen für die Struktur — z.B. eine Seite pro Heizkreis, oder Alarme separat?',
  units:     'Hallo! Ich schlage Einheiten für Datenpunkte ohne Unit vor. Gibt es Anlagen-spezifische Besonderheiten — z.B. Steigungswerte in K statt °C, oder Registerbereiche mit Sondereinheiten?',
  dashboard: 'Hallo! Ich entwerfe Dashboard-Seiten für deine Anlage. Was ist dir wichtig — schneller Überblick, Detail-Trends je Wärmepumpe, oder Energie-Fokus?',
};

export function AiConsole({ context, onTranscriptChange, onReady }: Props) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    { role: 'assistant', content: GREETING[context.mode] },
  ]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [ready, setReady] = useState(false);
  const listRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Every time the transcript changes, publish it up so the outer modal's
    // "Vorschlagen" click can include it as context. We drop the greeting
    // (index 0) — it carries no operator intent.
    const human = messages.slice(1);
    if (human.length === 0) { onTranscriptChange(''); return; }
    const compressed = human
      .filter((m) => !m.refused)
      .map((m) => `${m.role === 'user' ? 'Operator' : 'Assistant'}: ${m.content}`)
      .join('\n');
    onTranscriptChange(compressed);
  }, [messages, onTranscriptChange]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, thinking]);

  const send = async () => {
    const text = input.trim();
    if (text === '' || thinking) return;
    const next: ChatMessage[] = [...messages, { role: 'user', content: text }];
    setMessages(next);
    setInput('');
    setThinking(true);
    try {
      // History wire shape: user + assistant turns, oldest first, but drop
      // the greeting because it's not a real assistant turn (we invented it
      // client-side to feel welcoming).
      const wire = next.slice(1).map((m) => ({ role: m.role, content: m.content }));
      const res = await api.post<Reply>('/api/ai/chat/turn', {
        mode: context.mode,
        plantId: context.plantId ?? null,
        deviceId: context.deviceId ?? null,
        messages: wire,
      });
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: res.text,
        refused: !res.inScope,
      }]);
      if (res.readyToPropose && !ready) {
        setReady(true);
        onReady?.();
      }
    } catch (e) {
      const errText = e instanceof ApiError
        ? `Kommunikation fehlgeschlagen (${e.status}).`
        : e instanceof Error ? e.message : 'Unbekannter Fehler.';
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: errText,
        refused: true,
      }]);
    } finally {
      setThinking(false);
    }
  };

  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div class={`hv-ai-console${open ? ' hv-ai-console--open' : ''}`}>
      <button type="button" class="hv-ai-console__toggle" onClick={() => setOpen((o) => !o)}>
        <ChevronIcon />
        <span>Konsole {open ? 'schließen' : 'öffnen'} — mit {BRAND.aiName} besprechen</span>
      </button>
      {open && (
        <div class="hv-ai-console__body">
          <div class="hv-ai-console__messages" ref={listRef}>
            {messages.map((m, i) => (
              <div key={i}
                   class={`hv-ai-console__msg hv-ai-console__msg--${m.role === 'user' ? 'user' : 'ai'}${m.refused ? ' hv-ai-console__msg--refusal' : ''}`}>
                {m.role === 'assistant' && (
                  <span class="hv-ai-console__msg-avatar" aria-hidden="true"><MiniCloudIcon /></span>
                )}
                <span class="hv-ai-console__bubble">{m.content}</span>
              </div>
            ))}
            {thinking && <div class="hv-ai-console__thinking">{BRAND.aiName} denkt</div>}
          </div>
          {ready && (
            <div class="hv-ai-console__ready">
              {BRAND.aiName} hat genug Kontext — du kannst jetzt „Vorschlagen“ klicken.
            </div>
          )}
          <div class="hv-ai-console__input-row">
            <textarea
              class="hv-ai-console__input"
              value={input}
              onInput={(e) => setInput((e.target as HTMLTextAreaElement).value)}
              onKeyDown={onKeyDown}
              placeholder="Frage stellen oder Präferenz nennen. Enter = Senden, Shift+Enter = neue Zeile."
              rows={1}
            />
            <button type="button" class="hv-ai-console__send" onClick={send} disabled={thinking || input.trim() === ''}>
              Senden
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ChevronIcon() {
  return (
    <svg class="hv-ai-console__toggle-chevron" width="12" height="12" viewBox="0 0 20 20"
         fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
      <path d="M7 4l6 6-6 6" />
    </svg>
  );
}

function MiniCloudIcon() {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" stroke="none">
      <path d="M6 14a4 4 0 1 1 .7-7.94A5 5 0 0 1 16 8.5a3.5 3.5 0 0 1-.5 6.99H6z" />
    </svg>
  );
}
